import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { getMyIpList, getMyIpSummary } from '$/domains/myIp/myIpService';
import type { MyIpItem, MyIpSummary } from '$/domains/myIp/types';
import { subscribeMyIpChanged } from '$/domains/myIp/changeEvents';

export interface MyIpListState {
  scopeKey: string;
  status: 'idle' | 'loading' | 'allowed' | 'empty' | 'denied' | 'error';
  summary?: MyIpSummary;
  list: MyIpItem[];
  total: number;
  hasMore: boolean;
  loadingMore: boolean;
  error?: unknown;
  moreError?: unknown;
}

/** 空状态不伪造统计；scopeKey 由当前账号、Client-OID 和 clientId 共同组成。 */
function emptyState(scopeKey: string, status: MyIpListState['status']): MyIpListState {
  return {
    scopeKey, status, list: [], total: 0, hasMore: false, loadingMore: false,
  };
}

/** 无个人角色 IP 时保留列表页壳和服务端统计，供用户从首页查看并创建首个角色。 */
function emptyListState(scopeKey: string, summary: MyIpSummary): MyIpListState {
  return { ...emptyState(scopeKey, 'empty'), summary };
}

/** 仅当服务端统计明确为空时识别为空态，避免把已有 IP 的撤权误当成可访问列表。 */
function isEmptySummary(summary: MyIpSummary): boolean {
  return summary.ipCount === 0 && summary.cumulativeProjectCount === 0;
}

/** 按服务端顺序追加唯一 IP，不对局部分页排序，也不丢失稳定卡位。 */
export function appendUniqueMyIps(current: MyIpItem[], incoming: MyIpItem[]): MyIpItem[] {
  const seen = new Set(current.map((item) => item.ipId));
  const list = [...current];
  incoming.forEach((item) => {
    if (!seen.has(item.ipId)) {
      seen.add(item.ipId);
      list.push(item);
    }
  });
  return list;
}

/** 资格、首屏和分页局部管理；切换账号/空间的同一 render 即隐藏旧数据。 */
export function useMyIpList(scopeKey: string, enabled: boolean) {
  const [state, setState] = useState<MyIpListState>(() => emptyState(scopeKey, 'idle'));
  const [revision, setRevision] = useState(0);
  const epoch = useRef(0);
  const context = useRef({ scopeKey, enabled });
  context.current = { scopeKey, enabled };
  const page = useRef(1);
  const receivedCount = useRef(0);
  const morePending = useRef(false);
  const controller = useRef<AbortController>();

  useEffect(() => {
    epoch.current += 1;
    const requestEpoch = epoch.current;
    const abort = new AbortController();
    controller.current = abort;
    page.current = 1;
    receivedCount.current = 0;
    morePending.current = false;
    setState(emptyState(scopeKey, enabled ? 'loading' : 'idle'));

    /** 请求版本与空间同时匹配才可提交，兼容传输层未响应取消的情况。 */
    const isCurrent = () => epoch.current === requestEpoch && !abort.signal.aborted
      && context.current.scopeKey === scopeKey && context.current.enabled;

    /** 无个人角色 IP 不是撤权，保留列表页供空态和创建入口展示。 */
    const load = async () => {
      let summary: MyIpSummary | undefined;
      try {
        summary = await getMyIpSummary(abort.signal);
        if (!isCurrent()) return;
        if (!summary.pageVisible) {
          setState(isEmptySummary(summary) ? emptyListState(scopeKey, summary) : emptyState(scopeKey, 'denied'));
          return;
        }
        const result = await getMyIpList({ signal: abort.signal });
        if (!isCurrent()) return;
        if (!result.pageVisible) {
          setState(emptyState(scopeKey, 'denied'));
          return;
        }
        receivedCount.current = result.list.length;
        setState({
          ...emptyState(scopeKey, 'allowed'),
          summary,
          list: appendUniqueMyIps([], result.list),
          total: result.total,
          hasMore: result.list.length > 0 && receivedCount.current < result.total,
        });
      } catch (error) {
        if (isCurrent()) setState({ ...emptyState(scopeKey, 'error'), summary, error });
      }
    };
    if (enabled) load();
    return () => { abort.abort(); };
  }, [scopeKey, enabled, revision]);

  /** 显式重试/创建完成刷新会立即失效旧请求，避免迟到的分页重新写入。 */
  const refresh = useCallback(() => {
    epoch.current += 1;
    controller.current?.abort();
    setState(emptyState(scopeKey, enabled ? 'loading' : 'idle'));
    setRevision((value) => value + 1);
  }, [scopeKey, enabled]);

  useEffect(() => subscribeMyIpChanged(refresh), [refresh]);

  /** 加载更多失败保留已有内容及页码，重试仍请求同一页。 */
  const loadMore = useCallback(async () => {
    if (!enabled || state.scopeKey !== scopeKey || state.status !== 'allowed'
      || !state.hasMore || morePending.current || controller.current?.signal.aborted) return;
    morePending.current = true;
    const requestEpoch = epoch.current;
    const abort = controller.current;
    const nextPage = page.current + 1;
    /** 刷新或切换空间后丢弃分页结果与错误。 */
    const isCurrent = () => epoch.current === requestEpoch && !abort?.signal.aborted
      && context.current.scopeKey === scopeKey && context.current.enabled;
    setState((previous) => ({ ...previous, loadingMore: true, moreError: undefined }));
    try {
      const result = await getMyIpList({ page: nextPage, signal: abort?.signal });
      if (!isCurrent()) return;
      if (!result.pageVisible) {
        setState(emptyState(scopeKey, 'denied'));
        return;
      }
      page.current = nextPage;
      receivedCount.current += result.list.length;
      setState((previous) => ({
        ...previous,
        list: appendUniqueMyIps(previous.list, result.list),
        total: result.total,
        hasMore: result.list.length > 0 && receivedCount.current < result.total,
        loadingMore: false,
      }));
    } catch (moreError) {
      if (isCurrent()) setState((previous) => ({ ...previous, loadingMore: false, moreError }));
    } finally {
      if (isCurrent()) morePending.current = false;
    }
  }, [enabled, scopeKey, state.scopeKey, state.status, state.hasMore]);

  const visibleState = enabled && state.scopeKey === scopeKey
    ? state : emptyState(scopeKey, enabled ? 'loading' : 'idle');
  return {
    ...visibleState, loadedPages: visibleState.status === 'allowed' ? page.current : 0, refresh, loadMore,
  };
}
